import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PageHeader from './PageHeader'
import { FixtureRow } from './fixturesPage/FixtureRow'
import { standingTierFor } from './fixturesPage/pickTier'
import { getGamesByGameweek, seasonDataVersion } from '../seasonData'
import {
  getGameweekEvents,
  type GameweekEventsByFixture,
  type LiveFixture,
} from '../api/football'
import { useMatchClock } from '../app/matchClock'
import { buildFixtureViews } from '../fixtures'
import { prefersReducedMotion } from '../scoreCountUp'
import type { SimGameweekState } from '../gameweekSimulation'
import type { FixtureView, Game, GameweekDetail, Prediction } from '../types'
import './FixturesPage.css'

interface FixturesPageProps {
  currentGameweek: number
  /** Current gameweek's games, kickoffs already anchored onto real time. */
  currentGameweekGames: Game[]
  /** Current gameweek's predictions — the same store GameweekGrid writes to. */
  predictions: Record<string, Prediction>
  /** Current gameweek's ground-truth results (mockActualResultsByGameweek). */
  actualResults: Record<string, { homeScore: number; awayScore: number }>
  /** Dev "reveal results" toggle for the current gameweek. */
  resultsRevealed: boolean
  /** Non-null only while the current gameweek's dev-tool simulation is running. */
  simState: SimGameweekState | null
  /** Finalized past gameweeks — authoritative snapshot of games/predictions/results. */
  seasonDetails: Record<number, GameweekDetail>
  /**
   * In-play fixtures from the last `/api/live` poll — the same shared truth the game
   * cards run on. Without it this page could only ever see the mock sim, so a real match in
   * progress showed as upcoming until it settled. Empty in mock mode.
   */
  liveFixtures?: LiveFixture[]
  /** The `serverTime` that snapshot was taken at, anchoring the ticking match minute. */
  liveServerTime?: string | null
  /**
   * The fixture the user asked for by pressing its live or settled card on the predict grid.
   * Its row is scrolled to the centre of the screen and washed once in the colour of
   * where the pick stands. It is NOT expanded: the timeline stays behind the row's own tap.
   *
   * Read as a prop *change*, never on mount — this page is often already mounted as the drag-nav's
   * swipe neighbour, so a mount-time read would fire for the wrong fixture or never fire at all.
   */
  focusFixtureId?: string | null
  /** Called once the focus above has been acted on, so leaving and returning later does not
   *  re-scroll and re-flash the same row. */
  onFocusConsumed?: () => void
}

/**
 * How long after the arrival scroll starts before the wash does. Not zero, which reads as part of
 * the tap; not "when the scroll ends", which lagged. It overlaps the scroll's tail, so the colour
 * is still rising as the row settles.
 */
const WASH_START_MS = 160

function dayLabelFor(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  })
}

/** A fixture's scoreline as a stable string, whichever state it is in. */
function scoreOf(view: FixtureView): string {
  const score = view.liveScore ?? view.finalScore
  return score ? `${score.homeScore}-${score.awayScore}` : ''
}

function groupByLocalDay(views: FixtureView[]): [string, FixtureView[]][] {
  const sorted = [...views].sort(
    (a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime()
  )
  const groups = new Map<string, FixtureView[]>()
  for (const view of sorted) {
    const label = dayLabelFor(view.kickoff)
    const existing = groups.get(label)
    if (existing) existing.push(view)
    else groups.set(label, [view])
  }
  return [...groups.entries()]
}

function FixturesPage({
  currentGameweek,
  currentGameweekGames,
  predictions,
  actualResults,
  resultsRevealed,
  simState,
  seasonDetails,
  liveFixtures,
  liveServerTime = null,
  focusFixtureId = null,
  onFocusConsumed,
}: FixturesPageProps) {
  const [viewedGameweek, setViewedGameweek] = useState(currentGameweek)
  /**
   * Which fixture's match-events timeline is open — at most one, so an expanded
   * timeline never pushes the rest of the day off the screen. A collapsed row fetches nothing.
   *
   * Cleared when the browsed gameweek changes: the id would not match anything in the new list
   * anyway, and leaving it set means a row silently reopening if you browse back.
   */
  const [expandedFixtureId, setExpandedFixtureId] = useState<string | null>(null)

  // Bounds come from the loaded schedule, not from a module-scope read. The season
  // store starts empty and is filled by `loadSeasonData()` after this module is imported, so
  // anything captured at import time is `[]` — and `gw <= undefined` is always false, which left
  // both arrows live and let browsing run off both ends of the season. `seasonDataVersion()` bumps
  // on every store replacement, so this recomputes when the season lands.
  const seasonVersion = seasonDataVersion()
  const { minGameweek, maxGameweek } = useMemo(() => {
    void seasonVersion
    const numbers = Object.keys(getGamesByGameweek()).map(Number)
    // No schedule yet: pin both bounds to the current gameweek so both arrows are disabled.
    if (numbers.length === 0) return { minGameweek: currentGameweek, maxGameweek: currentGameweek }
    return { minGameweek: Math.min(...numbers), maxGameweek: Math.max(...numbers) }
  }, [seasonVersion, currentGameweek])

  const views = useMemo(() => {
    if (viewedGameweek === currentGameweek) {
      return buildFixtureViews({
        games: currentGameweekGames,
        predictions,
        actualResults,
        resultsRevealed,
        simState,
        // Only the current gameweek: the snapshot only ever reports fixtures in play, and a past
        // gameweek's rows are settled by definition.
        liveFixtures,
      })
    }

    const finalized = seasonDetails[viewedGameweek]
    if (finalized) {
      return buildFixtureViews({
        games: finalized.games,
        predictions: finalized.predictions,
        actualResults: finalized.results,
        resultsRevealed: true,
        simState: null,
      })
    }

    return buildFixtureViews({
      games: getGamesByGameweek()[viewedGameweek] ?? [],
      predictions: {},
      actualResults: {},
      resultsRevealed: false,
      simState: null,
    })
  }, [
    viewedGameweek,
    currentGameweek,
    currentGameweekGames,
    predictions,
    actualResults,
    resultsRevealed,
    simState,
    seasonDetails,
    liveFixtures,
  ])

  const dayGroups = useMemo(() => groupByLocalDay(views), [views])

  // One clock for the whole page: a ten-fixture gameweek must not run ten
  // intervals. Passing `null` while nothing the server reported is in play means no interval runs.
  const hasServerLive = views.some((view) => view.liveStatus !== null)
  const { elapsedMs: liveElapsedMs } = useMatchClock(hasServerLive ? liveServerTime : null)

  /**
   * One request per viewed gameweek for every fixture's goals and sendings-off, not one per row.
   * Carries the previous ETag, so an unchanged gameweek costs a 304 and no re-render.
   *
   * Keyed on statuses and scores, not `views`, which is a fresh identity every poll because the
   * minute ticks. This key moves on a goal or a whistle.
   */
  const liveShape = views.map((view) => `${view.id}:${view.status}:${scoreOf(view)}`).join(',')
  /**
   * The second reason to re-ask, the one the score can't cover: during a match the events request
   * rides the live poll.
   *
   * A sending-off changes no scoreline, and a *scorer* lands a beat after the goal (the name costs
   * a second provider request), so keyed on the score alone both would wait for the next goal.
   * `serverTime` moves on every non-304 `/live` response; `null` when nothing is in play, so a
   * settled gameweek makes exactly one request.
   */
  const eventsHeartbeat = hasServerLive ? liveServerTime : null
  const [gameweekEvents, setGameweekEvents] = useState<GameweekEventsByFixture>({})
  const eventsEtag = useRef<string | null>(null)
  /**
   * The gameweek whose events request has come back, whatever it came back with. The goals it adds
   * grow the collapsed rows, so this is the moment the list's heights stop moving — which is what
   * an arrival scroll has to wait for to be one motion rather than two.
   */
  const [eventsSettledFor, setEventsSettledFor] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await getGameweekEvents(viewedGameweek, eventsEtag.current)
        if (cancelled) return
        eventsEtag.current = response.etag
        if (response.events) setGameweekEvents(response.events)
      } catch {
        // Leave what is already on screen alone. This now runs once a minute through a match, so
        // blanking on a failure would take a rendered timeline down over a single blip; and the
        // only case that needs clearing, a change of gameweek, is cleared by `browseGameweek`.
      } finally {
        if (!cancelled) setEventsSettledFor(viewedGameweek)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [viewedGameweek, liveShape, eventsHeartbeat])

  const browseGameweek = useCallback((next: (current: number) => number) => {
    setViewedGameweek(next)
    setExpandedFixtureId(null)
    setEventsSettledFor(null)
    // The ETag belongs to the gameweek it came from; carrying it across would 304 the new one
    // against the old one's rows.
    eventsEtag.current = null
    setGameweekEvents({})
  }, [])

  /** Arriving from a pressed game card — the row to scroll to. */
  const [focus, setFocus] = useState<{ id: string; nonce: number } | null>(null)
  /**
   * The blink, set once the scroll has actually delivered the row — split from `focus` because a
   * wash starting with the scroll had already faded for rows near the bottom.
   *
   * `nonce` re-keys the row on every arrival: a CSS animation left in place does not replay. Null
   * under reduced motion, where a colour that appeared and never left is worse than none.
   */
  const [wash, setWash] = useState<{ id: string; nonce: number } | null>(null)
  const rowElements = useRef(new Map<string, HTMLDivElement>())
  const registerRow = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) rowElements.current.set(id, el)
      else rowElements.current.delete(id)
    },
    []
  )

  // Which focus id has already been acted on. Without it a parent that never clears the prop would
  // re-flash the row on every live poll, since `views` is a fresh identity each time.
  const consumedFocus = useRef<string | null>(null)
  useEffect(() => {
    if (!focusFixtureId) {
      consumedFocus.current = null
      return
    }
    if (consumedFocus.current === focusFixtureId) return

    // The predict grid is always the current gameweek, but the user may have browsed away and left
    // the page on GW7. Snap back first and let the next render (with the right rows) do the work —
    // deliberately without marking this consumed.
    if (!views.some((view) => view.id === focusFixtureId)) {
      if (viewedGameweek !== currentGameweek) {
        // Reacting to a prop change, not synchronising on mount: the arrival IS the event, and the
        // snap has to happen before the row it names can exist to scroll to.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        browseGameweek(() => currentGameweek)
        return
      }
      consumedFocus.current = focusFixtureId
      onFocusConsumed?.()
      return
    }

    consumedFocus.current = focusFixtureId
    setFocus((previous) => ({ id: focusFixtureId, nonce: (previous?.nonce ?? 0) + 1 }))
    onFocusConsumed?.()
  }, [focusFixtureId, views, viewedGameweek, currentGameweek, browseGameweek, onFocusConsumed])

  /** The arrival whose scroll has already been made, so waiting for heights can't re-trigger it. */
  const scrolledFor = useRef<number | null>(null)

  // Separate from the effect above so it runs against a row that is already mounted and has
  // registered itself.
  useEffect(() => {
    if (!focus || scrolledFor.current === focus.nonce) return
    // ONE motion, not two. The gameweek's goals grow every collapsed row, so a scroll issued before
    // that request lands is aimed at heights that are about to change — and correcting afterwards
    // reads as the page scrolling twice. Wait for the final heights and go once.
    if (eventsSettledFor !== viewedGameweek) return
    const element = rowElements.current.get(focus.id)
    if (!element) return

    scrolledFor.current = focus.nonce
    const reduced = prefersReducedMotion()
    element.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' })
    if (reduced) return

    const start = setTimeout(() => setWash({ id: focus.id, nonce: focus.nonce }), WASH_START_MS)
    return () => clearTimeout(start)
  }, [focus, eventsSettledFor, viewedGameweek])

  return (
    <div className="fixtures-page">
      <PageHeader title="Fixtures" />

      <div className="fixtures-page__gwnav">
        <button
          type="button"
          className="fixtures-page__gwnav-arrow"
          disabled={viewedGameweek <= minGameweek}
          onClick={() => browseGameweek((gw) => Math.max(minGameweek, gw - 1))}
          aria-label="Previous gameweek"
        >
          &lsaquo;
        </button>
        <span className="fixtures-page__gwnav-label">Gameweek {viewedGameweek}</span>
        <button
          type="button"
          className="fixtures-page__gwnav-arrow"
          disabled={viewedGameweek >= maxGameweek}
          onClick={() => browseGameweek((gw) => Math.min(maxGameweek, gw + 1))}
          aria-label="Next gameweek"
        >
          &rsaquo;
        </button>
      </div>

      {viewedGameweek !== currentGameweek && (
        <div className="fixtures-page__subnote">
          <button
            type="button"
            className="fixtures-page__jump-current"
            onClick={() => browseGameweek(() => currentGameweek)}
          >
            Jump to current
          </button>
        </div>
      )}

      {dayGroups.map(([label, rows]) => (
        <div key={label}>
          <div className="fixtures-page__day">
            <span className="fixtures-page__day-rule" />
            <span className="fixtures-page__day-label label-section">{label}</span>
            <span className="fixtures-page__day-rule" />
          </div>
          {rows.map((view) => {
            const washed = wash?.id === view.id
            return (
              <FixtureRow
                // Re-keyed on arrival so the wash animation restarts even for the same fixture.
                key={washed ? `${view.id}:wash-${wash.nonce}` : view.id}
                view={view}
                rowRef={registerRow(view.id)}
                // Read from the CURRENT view, not frozen at arrival: a live fixture's standing is
                // where the pick stands right now, and the colour follows the score as it changes.
                washTier={washed ? (standingTierFor(view) ?? 'none') : undefined}
                redCards={gameweekEvents[view.id]?.redCards}
                goals={gameweekEvents[view.id]?.goals}
                liveElapsedMs={liveElapsedMs}
                expanded={expandedFixtureId === view.id}
                onToggle={
                  view.status === 'upcoming'
                    ? undefined
                    : () => setExpandedFixtureId((open) => (open === view.id ? null : view.id))
                }
              />
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default FixturesPage
